export type PresenceUpdateCoordinator = {
  request: () => Promise<void>;
  dispose: () => void;
};

/** Auth failures are terminal: keep retrying a 401 loops forever and burns Apache workers. */
export function isPresenceUpdateRetryableError(error: unknown): boolean {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status?: unknown }).status;
    if (status === 401 || status === 403) {
      return false;
    }
  }
  return true;
}

/**
 * Coalesces update requests while preserving the latest state read by `run`.
 * Every caller resolves only after the pending update has been successfully flushed,
 * or after a non-retryable failure (for example HTTP 401).
 */
export function createPresenceUpdateCoordinator(options: {
  run: () => Promise<void>;
  retryMs?: number;
  shouldRetry?: (error: unknown) => boolean;
  setTimer?: (callback: () => void, delayMs: number) => number;
  clearTimer?: (timer: number) => void;
}): PresenceUpdateCoordinator {
  const retryMs = options.retryMs ?? 5_000;
  const shouldRetry = options.shouldRetry ?? isPresenceUpdateRetryableError;
  const setTimer = options.setTimer ?? ((callback, delayMs) => window.setTimeout(callback, delayMs));
  const clearTimer = options.clearTimer ?? ((timer) => window.clearTimeout(timer));

  let pending = false;
  let running = false;
  let disposed = false;
  let retryTimer: number | undefined;
  let waiters: Array<{ resolve: () => void }> = [];

  const settleWaiters = (): void => {
    const current = waiters;
    waiters = [];
    for (const waiter of current) {
      waiter.resolve();
    }
  };

  const flush = async (): Promise<void> => {
    if (disposed || running) {
      return;
    }
    running = true;
    try {
      while (pending && !disposed) {
        pending = false;
        try {
          await options.run();
        } catch (error) {
          if (!shouldRetry(error)) {
            settleWaiters();
            return;
          }
          pending = true;
          if (retryTimer == null) {
            retryTimer = setTimer(() => {
              retryTimer = undefined;
              void flush();
            }, retryMs);
          }
          return;
        }
      }
      if (!pending) {
        settleWaiters();
      }
    } finally {
      running = false;
      if (pending && retryTimer == null && !disposed) {
        void flush();
      }
    }
  };

  return {
    request: () => {
      if (disposed) {
        return Promise.resolve();
      }
      pending = true;
      const promise = new Promise<void>((resolve) => {
        waiters.push({ resolve });
      });
      void flush();
      return promise;
    },
    dispose: () => {
      disposed = true;
      pending = false;
      if (retryTimer != null) {
        clearTimer(retryTimer);
        retryTimer = undefined;
      }
      settleWaiters();
    },
  };
}
